"use strict";
// Connector catalog shared between server (catalog endpoint) and client (directory grid).
// Built-ins: Atlassian Jira, Atlassian Confluence, Salesforce (ICRM), TestRail,
// Outlook, Microsoft Teams (the set grows over time — see CONNECTORS below).
// OAuth providers go through the /v1/connectors/:provider/authorize + popup flow.
// Jira, Confluence, TestRail and Zephyr Scale are api-key: credentials entered
// inline in the modal (Atlassian uses an API token + base URL, shared per workspace).
//
// ─────────────────────────────────────────────────────────────────────────────
// ## Architectural intent (v2 extension plan — seams only, not implemented)
// ─────────────────────────────────────────────────────────────────────────────
// The v1 catalog is a static list of first-party built-ins. The shape below is
// designed so that v2 can introduce **user-defined connectors** — including
// user-registered **MCP servers** — without restructuring callers. The
// extension points are:
//
//   1. `source` — `'builtin'` today; v2 widens to `'user' | 'tenant'` for
//      rows persisted by individual users / tenant admins.
//
//   2. `authKind` — `'oauth' | 'api-key'` today; v2 adds `'mcp'` so the
//      transport itself is the auth boundary (the connected MCP server's
//      handshake gates everything).
//
//   3. `mcp?` — placeholder field for the v2 MCP entries. Built-ins leave
//      it undefined; user-registered MCPs will populate
//      `{ serverUrl, transport }`.
//
//   4. `icon` — separate from `id`. Built-ins reuse their id as the icon
//      key (`'jira'` → JiraMark). v2 user connectors set this to a generic
//      fallback ('generic') or upload custom marks later.
//
//   5. `BuiltinConnectorId` is preserved as a strict literal subset of
//      `ConnectorId`. In v2 `ConnectorId` widens to
//      `BuiltinConnectorId | string` so server-side code paths that only
//      handle first-party flows can still narrow.
//
//   6. Catalog source — server's `ConnectorCatalogService` owns the list.
//      v1 returns only `CONNECTORS`; v2 merges builtins with rows from a
//      `connector_definitions` table scoped by (tenantId, userId).
//
// Do NOT introduce these capabilities now. Keep the seams; ship the built-ins.
// ─────────────────────────────────────────────────────────────────────────────
Object.defineProperty(exports, "__esModule", { value: true });
exports.CONNECTORS = void 0;
exports.supportsConnectorWrite = supportsConnectorWrite;
/** Whether a connector supports granting write access. */
function supportsConnectorWrite(provider) {
    return provider !== 'powerbi';
}
exports.CONNECTORS = [
    {
        id: 'jira',
        name: 'Jira',
        vendor: 'Atlassian',
        authKind: 'api-key',
        description: 'Find issues, track sprint progress, and pull ticket context straight into chat.',
        source: 'builtin',
    },
    {
        id: 'confluence',
        name: 'Confluence',
        vendor: 'Atlassian',
        authKind: 'api-key',
        description: 'Pull team docs, runbooks, and design specs from Confluence pages and spaces.',
        source: 'builtin',
    },
    {
        id: 'salesforce',
        name: 'Salesforce (ICRM)',
        vendor: 'Salesforce',
        authKind: 'oauth',
        description: 'Surface accounts, opportunities, and case history from Customertimes ICRM.',
        source: 'builtin',
    },
    {
        id: 'testrail',
        name: 'TestRail',
        vendor: 'Gurock',
        authKind: 'api-key',
        description: 'Track test runs, failing cases, and QA progress from the Customertimes TestRail instance.',
        source: 'builtin',
    },
    {
        id: 'zephyr',
        name: 'Zephyr Scale',
        vendor: 'SmartBear',
        authKind: 'api-key',
        description: 'Create and execute Zephyr Scale test cases, cycles and plans straight from chat.',
        source: 'builtin',
    },
    {
        id: 'outlook',
        name: 'Outlook',
        vendor: 'Microsoft',
        authKind: 'oauth',
        description: 'Search mail, scan your calendar, and pull contact details straight into chat.',
        source: 'builtin',
    },
    {
        id: 'teams',
        name: 'Microsoft Teams',
        vendor: 'Microsoft',
        authKind: 'oauth',
        description: 'Read and send your Teams chats and channel messages straight from chat.',
        source: 'builtin',
    },
    {
        id: 'azure_devops',
        name: 'Azure DevOps',
        vendor: 'Microsoft',
        authKind: 'oauth',
        description: 'Access Azure Boards work items and Azure Test Plans on your behalf.',
        source: 'builtin',
    },
    {
        id: 'github',
        name: 'GitHub',
        vendor: 'GitHub',
        authKind: 'oauth',
        description: 'Access GitHub repositories, issues and pull requests on your behalf.',
        source: 'builtin',
    },
    {
        id: 'gitlab',
        name: 'GitLab',
        vendor: 'GitLab',
        authKind: 'api-key',
        description: 'Access GitLab projects, issues and merge requests on your behalf.',
        source: 'builtin',
    },
    {
        id: 'sharepoint',
        name: 'SharePoint',
        vendor: 'Microsoft',
        authKind: 'oauth',
        description: 'Search SharePoint sites, read the files inside, and edit them once write access is granted.',
        source: 'builtin',
    },
    {
        id: 'powerbi',
        name: 'Power BI',
        vendor: 'Microsoft',
        authKind: 'oauth',
        description: 'Read model information and query Power BI data with your own Microsoft account. Read-only access.',
        source: 'builtin',
        permission_level: 'read',
    },
];
//# sourceMappingURL=connector.js.map